import { sourceChannels } from "@purlink/shared/attribution";
import { followUpFilters } from "@purlink/shared/leads-api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Columns3, Plus, Search, SlidersHorizontal } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { PageHeader } from "~/components/shell/app-shell";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Alert, EmptyState, Skeleton } from "~/components/ui/feedback";
import { Input } from "~/components/ui/input";
import { toast } from "~/components/ui/toaster";
import { servicesQuery } from "~/features/leads/api";
import { AssignLeadDialog } from "~/features/leads/assign-dialog";
import {
  ActiveFilterChips,
  countFilters,
  FilterDialog,
  pickFilters,
} from "~/features/leads/filter-dialog";
import { MarkAllReviewedButton } from "~/features/leads/mark-all-reviewed";
import { LeadFormDialog } from "~/features/leads/lead-form-dialog";
import {
  fetchColumnPage,
  pipelineKeys,
  pipelineQuery,
  type PipelineColumn,
  type PipelineData,
  type PipelineFilters,
  type PipelineLead,
} from "~/features/pipeline/api";
import { Board } from "~/features/pipeline/board";
import { MobileBoard } from "~/features/pipeline/mobile-board";
import { useComposeEmail } from "~/features/emails/lazy-compose";
import { useMoveLead } from "~/features/pipeline/use-move-lead";
import { errorMessage } from "~/lib/auth";
import { useCan } from "~/lib/use-can";
import { useMediaQuery } from "~/lib/use-media-query";

const optional = z.string().optional().catch(undefined);
const flag = z.boolean().optional().catch(undefined);
const day = z.iso.date().optional().catch(undefined);

const searchSchema = z.object({
  q: optional,
  serviceId: optional,
  channel: z.enum(sourceChannels).optional().catch(undefined),
  campaign: optional,
  followUp: z.enum(followUpFilters).optional().catch(undefined),
  mine: flag,
  unreviewed: flag,
  assignee: optional,
  campaignId: optional,
  keyword: optional,
  createdFrom: day,
  createdTo: day,
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/pipeline")({
  validateSearch: searchSchema,
  component: PipelinePage,
});

function PipelinePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const can = useCan();
  const wide = useMediaQuery("(min-width: 768px)");
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState(search.q ?? "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [assigning, setAssigning] = useState<PipelineLead | null>(null);
  const onAssign = can("lead", "assign") ? setAssigning : undefined;

  const filters = search satisfies PipelineFilters;
  const pipeline = useQuery(pipelineQuery(filters));
  const services = useQuery(servicesQuery);
  const { moveTo, moveToStage } = useMoveLead(filters);
  const moves = { moveTo, moveToStage };
  const { compose, dialog: composeDialog } = useComposeEmail();
  const onSendEmail = can("email", "send") ? compose : undefined;

  const setSearch = (patch: Partial<Search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== query.trim()) void setSearch({ q: query.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only react to typing
  }, [query]);

  async function loadMore(column: PipelineColumn) {
    try {
      const page = await fetchColumnPage(filters, column);
      queryClient.setQueryData<PipelineData>(pipelineKeys.board(filters), (current) =>
        current
          ? {
              ...current,
              stages: current.stages.map((candidate) =>
                candidate.id === column.id
                  ? {
                      ...candidate,
                      leads: [
                        ...candidate.leads,
                        ...page.leads.filter(
                          (lead) => !candidate.leads.some((existing) => existing.id === lead.id),
                        ),
                      ],
                      nextCursor: page.nextCursor,
                      total: page.total,
                    }
                  : candidate,
              ),
            }
          : current,
      );
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  const applied = pickFilters(search);
  const activeFilterCount = countFilters(applied);
  const filtered = Boolean(search.q) || activeFilterCount > 0;
  const serviceList = services.data?.services ?? [];
  const totalLeads = pipeline.data?.stages.reduce((sum, column) => sum + column.total, 0) ?? 0;
  const stageCount = pipeline.data?.stages.length ?? 0;
  const hint = wide
    ? "Drag cards to change stage or priority."
    : "Use a card's move button to change its stage or priority.";

  return (
    <>
      <PageHeader
        title="Pipeline"
        description={
          pipeline.data
            ? `${totalLeads} lead${totalLeads === 1 ? "" : "s"}${filtered ? " match your filters" : ""} across ${stageCount} stage${stageCount === 1 ? "" : "s"}. ${hint}`
            : hint
        }
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus /> Add lead
          </Button>
        }
      />

      <Card padding="sm" className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
            aria-hidden
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name, email, company or phone"
            aria-label="Search leads"
            className="h-9 pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="h-9"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
          >
            <SlidersHorizontal /> Filter{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </Button>
          <MarkAllReviewedButton
            filters={filters}
            unreviewed={pipeline.data?.unreviewedTotal ?? 0}
            filtered={filtered}
          />
        </div>
      </Card>
      <div className="-mt-2 mb-4 empty:hidden">
        <ActiveFilterChips
          value={applied}
          services={serviceList}
          onRemove={(key) => void setSearch({ [key]: undefined })}
          onClearAll={() => {
            setQuery("");
            void navigate({ search: {}, replace: true });
          }}
        />
      </div>
      <FilterDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        value={applied}
        onApply={(next) => void setSearch(next)}
        services={serviceList}
      />

      {pipeline.isPending ? (
        <div className="flex gap-3 overflow-hidden" aria-busy="true" aria-label="Loading the board">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-96 w-[19rem] shrink-0 rounded-xl" />
          ))}
        </div>
      ) : pipeline.isError ? (
        <Alert tone="error" title="The board didn't load.">
          {errorMessage(pipeline.error)}{" "}
          <Button variant="link" size="sm" onClick={() => void pipeline.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : pipeline.data.stages.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Columns3 />}
            title="No stages yet"
            description="Add a stage in Settings → Stages to start using the pipeline."
          />
        </Card>
      ) : wide ? (
        <Board
          data={pipeline.data}
          filters={filters}
          moves={moves}
          canReorderStages={can("stage", "reorder")}
          onLoadMore={loadMore}
          onSendEmail={onSendEmail}
          onAssign={onAssign}
          query={search.q}
        />
      ) : (
        <MobileBoard
          data={pipeline.data}
          moves={moves}
          onLoadMore={loadMore}
          onSendEmail={onSendEmail}
          onAssign={onAssign}
          query={search.q}
        />
      )}

      <LeadFormDialog open={addOpen} onOpenChange={setAddOpen} />
      <AssignLeadDialog lead={assigning} onOpenChange={(open) => !open && setAssigning(null)} />
      {composeDialog}
    </>
  );
}
